import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from '../../utils/invoice-pdf-general-section-utilities';
import {
    Box,
    Typography,
    Grid,
    Button,
    Chip,
    IconButton,
    Tooltip,
    Avatar,
    Tab,
    Tabs,
    Paper,
    Divider,
    LinearProgress,
    Badge,
    Alert,
    TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, Autocomplete, InputAdornment, Stack,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { useProducts } from '../../hooks/useProducts';
import {
    Inventory as InventoryIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    FilterList as FilterIcon,
    Download as ExportIcon,
    SwapHoriz as MovementIcon,
    LocationOn as LocationIcon,
    Warning as WarningIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    CheckCircle as CheckCircleIcon,
    History as HistoryIcon,
    Assignment as AdjustIcon,
    Warehouse as WarehouseIcon,
    Visibility as ViewIcon,
    Edit as EditIcon,
    Info as InfoIcon,
    ViewKanban as KanbanIcon,
    TableChart as TableIcon
} from '@mui/icons-material';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import SmartDataGrid from '../../components/common/SmartDataGrid';
import GlassSidebar from '../../components/common/GlassSidebar';
import KanbanView from '../../components/ui/KanbanView';
import {
    useInventory,
    useInventoryStatCards,
    useAdjustStock,
    useInventoryMovements,
    useInventoryAlerts,
    useCycleCountStatus
} from '../../hooks/useInventory';
import { useLocationStats } from '../../hooks/useLocations';
import { useWarehouses } from '../../hooks/useWarehouses';

const INVENTORY_STATUS_KANBAN_COLUMNS = [
    { id: 'available', label: 'Available', color: '#01B574' },
    { id: 'low_stock', label: 'Low Stock', color: '#FFB547' },
    { id: 'out_of_stock', label: 'Out of Stock', color: '#EE5D50' }
];

/**
 * KPI Card - Aurora 2026 Style
 */
const KPICard = ({ title, value, icon: Icon, trend, color, subtitle, isLoading }) => {
    const isPositive = trend && trend.startsWith('+');

    return (
        <Motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
        >
            <Paper
                sx={{
                    p: 2.5,
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        background: color
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ p: 1, borderRadius: 2, background: `${color}15` }}>
                        <Icon sx={{ color, fontSize: 24 }} />
                    </Box>
                    {trend && (
                        <Chip
                            label={trend}
                            size="small"
                            sx={{
                                bgcolor: isPositive ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                color: isPositive ? '#4caf50' : '#f44336',
                                fontWeight: 700,
                                fontSize: '0.75rem'
                            }}
                        />
                    )}
                </Box>

                {isLoading ? (
                    <Box sx={{ py: 1 }}><LinearProgress color="inherit" sx={{ opacity: 0.1 }} /></Box>
                ) : (
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                        {value}
                    </Typography>
                )}

                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                    {title}
                </Typography>

                {subtitle && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        {subtitle}
                    </Typography>
                )}
            </Paper>
        </Motion.div>
    );
};

/**
 * Inventory Cockpit Page - Unified Master Data & Operational Hub
 */
const InventoryCockpitPage = () => {
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [selectedCurrency, _setSelectedCurrency] = useState('EUR');
    const locale = i18n?.language || 'en-US';

    const formatCurrency = useCallback((amount) => {
        const symbol = getCurrencySymbol(selectedCurrency);
        return `${symbol}${(amount || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, [selectedCurrency, locale]);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [sidebarTab, setSidebarTab] = useState(0);
    const [warehouseFilter, setWarehouseFilter] = useState('all');
    const [stockStatusFilter, setStockStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('table');

    // Data Fetching
    const { data: inventoryData, isLoading: invLoading, refetch: refetchInv } = useInventory({ 
        limit: 100,
        warehouse_id: warehouseFilter !== 'all' ? warehouseFilter : undefined,
        stock_status: stockStatusFilter !== 'all' ? stockStatusFilter : undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        search: searchTerm || undefined
    });
    const { data: statCards, isLoading: statsLoading } = useInventoryStatCards();
    const { data: movementsData, isLoading: movementsLoading } = useInventoryMovements();
    const { data: alertsData } = useInventoryAlerts();
    const { data: locationStats } = useLocationStats();
    const { data: warehouses } = useWarehouses();
    const { data: productsData } = useProducts({ limit: 100 });
    const { data: cycleCountData } = useCycleCountStatus();
    const adjustMutation = useAdjustStock();
    
    const stockAccuracy = useMemo(() => {
        const accuracy = cycleCountData?.accuracy ?? cycleCountData?.stock_accuracy;
        if (accuracy !== undefined && accuracy !== null) {
            return `${(accuracy * 100).toFixed(1)}%`;
        }
        return cycleCountData?.accuracy_percentage ?? '—';
    }, [cycleCountData]);

    const [adjustOpen, setAdjustOpen] = useState(false);
    const [adjustForm, setAdjustForm] = useState({
        product: null,
        type: 'add',
        quantity: 0,
        reason: 'Manual Adjustment'
    });

    const handleAdjustSubmit = async () => {
        if (!adjustForm.product || adjustForm.quantity <= 0) return;
        try {
            let finalQty = parseInt(adjustForm.quantity);
            if (adjustForm.type === 'remove') finalQty = -finalQty;
            
            await adjustMutation.mutateAsync({
                product_id: adjustForm.product.id,
                quantity: finalQty,
                reason: adjustForm.reason,
                operator_id: 'current_user'
            });
            setAdjustOpen(false);
            setAdjustForm({ product: null, type: 'add', quantity: 0, reason: 'Manual Adjustment' });
            refetchInv();
        } catch (err) {
            console.error("Adjustment failed", err);
        }
    };


    const inventoryItems = useMemo(() => inventoryData?.items || [], [inventoryData]);
    const movements = useMemo(() => movementsData?.movements || [], [movementsData]);

    const getStockStatus = useCallback((qty, minStock = 10) => {
        if (qty <= 0) return 'out_of_stock';
        if (qty <= minStock) return 'low_stock';
        return 'available';
    }, []);

    const kanbanInventory = useMemo(() => {
        return inventoryItems.map(item => ({
            id: item.id,
            title: item.name || `Item #${item.id}`,
            subtitle: `${item.sku || 'No SKU'} | ${item.warehouse_name || 'Unknown warehouse'}`,
            status: getStockStatus(item.quantity, item.min_stock),
            metadata: {
                quantity: item.quantity,
                min_stock: item.min_stock,
                location_code: item.location_code,
                total_value: item.total_value
            }
        }));
    }, [inventoryItems, getStockStatus]);

    const handleViewModeChange = useCallback((event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    }, []);

    const handleRowClick = (item) => {
        setSelectedItem(item);
        setSidebarOpen(true);
    };

    const getStockStatusColor = (qty, minStock = 10) => {
        if (qty <= 0) return '#f44336';
        if (qty <= minStock) return '#ff9800';
        return '#4caf50';
    };

        const columns = [
            {
                id: 'product',
                label: t('common.product', 'Product'),
                width: '30%',
            render: (val, row) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                        src={row.image_url}
                        sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 700 }}
                    >
                        {row.name ? row.name[0] : 'P'}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.name || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.sku || '-'}</Typography>
                    </Box>
                </Box>
            )
        },
                {
                    id: 'warehouse',
                    label: t('common.warehouse', 'Warehouse'),
                    render: (val, row) => (
                <Chip
                    icon={<WarehouseIcon sx={{ fontSize: '1rem !important' }} />}
                    label={row.warehouse_name || '-'}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                />
            )
        },
                {
                    id: 'location',
                    label: t('common.location', 'Location'),
                    render: (val, row) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                        {row.location_code || 'Pick-Zone-01'}
                    </Typography>
                </Box>
            )
        },
                {
                    id: 'quantity',
                    label: t('common.quantity', 'Quantity'),
                    align: 'right',
            render: (val, row) => (
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: getStockStatusColor(row.quantity, row.min_stock) }}>
                        {(row.quantity || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {row.unit || 'pcs'}
                    </Typography>
                </Box>
            )
        },
                {
                    id: 'value',
                    label: t('common.value', 'Value'),
                    align: 'right',
                    render: (val, row) => (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(row.total_value)}
                        </Typography>
                    )
                },
                        {
                            id: 'status',
                    label: t('common.status', 'Status'),
                    render: (val, row) => {
                const qty = row.quantity || 0;
                const min = row.min_stock || 10;
                if (qty <= 0) return <Chip label={t('common.inventory.outOfStock', 'Out of Stock')} size="small" color="error" />;
                if (qty <= min) return <Chip label={t('common.inventory.lowStock', 'Low Stock')} size="small" color="warning" />;
                return <Chip label={t('common.inventory.available', 'Available')} size="small" color="success" />;
            }
        }
    ];

    return (
        <Box sx={{ p: 3, position: 'relative' }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                <Box>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 800,
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            letterSpacing: '-0.03em',
                            mb: 0.5
                        }}
                    >
                        {t('common.inventory.cockpitTitle', 'Inventory Cockpit')}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {t('common.inventory.cockpitSubtitle', 'Unified orchestration of global stock levels, movements, and fulfillment health.')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => refetchInv()}
                        sx={{ borderRadius: 2.5, px: 2, height: 42 }}
                    >
                        {t('common.refresh', 'Refresh')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={{
                            borderRadius: 2.5,
                            px: 3,
                            height: 42,
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            boxShadow: '0 8px 20px -6px rgba(245, 87, 108, 0.5)'
                        }}
                        onClick={() => setAdjustOpen(true)}
                    >
                        {t('common.inventory.adjustStock', 'Adjust Stock')}
                    </Button>
                </Box>
            </Box>

            {/* KPI Grid */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title={t('common.inventory.totalInventoryValue', 'Total Inventory Value')}
                        value={formatCurrency(statCards?.[1]?.value || 0)}
                        icon={TrendingUpIcon}
                        trend={statCards?.[1]?.trend || ''}
                        color="#4caf50"
                        isLoading={statsLoading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title={t('common.inventory.stockAccuracy', 'Stock Accuracy')}
                        value={stockAccuracy}
                        icon={CheckCircleIcon}
                        trend={cycleCountData?.accuracy_trend || ''}
                        color="#2196f3"
                        subtitle={t('common.inventory.basedOnCycleCounts', 'Based on last 30 days cycle counts')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title={t('common.inventory.occupancyRate', 'Occupancy Rate')}
                        value={`${locationStats?.occupancy_rate || 0}%`}
                        icon={WarehouseIcon}
                        trend={locationStats?.occupancy_trend || ''}
                        color="#ff9800"
                        subtitle={`${locationStats?.occupied || 0}/${locationStats?.total || 0} locations occupied`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title={t('common.inventory.criticalAlerts', 'Critical Alerts')}
                        value={alertsData?.total || 0}
                        icon={WarningIcon}
                        color="#f44336"
                        subtitle={alertsData?.total > 0 ? t('common.inventory.stockOutsAndExpiration', 'Stock-outs & Expiration risks') : t('common.inventory.noCriticalAlerts', 'No critical alerts')}
                    />
                </Grid>
            </Grid>

            {/* Main Content Area */}
            <Paper
                sx={{
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.02)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{
                            px: 2,
                            '& .MuiTab-root': {
                                minHeight: 64,
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                textTransform: 'none',
                            }
                        }}
                    >
                        <Tab icon={<InventoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('common.inventory.stockOverview', 'Stock Overview')} />
                        <Tab icon={<MovementIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('common.inventory.realtimeMovements', 'Real-time Movements')} />
                        <Tab icon={<LocationIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('common.inventory.warehouseTopography', 'Warehouse Topography')} />
                        <Tab icon={<AdjustIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={t('common.inventory.adjustmentsAndAudits', 'Adjustments & Audits')} />
                    </Tabs>
                </Box>

                <Box sx={{ p: 0 }}>
                    <AnimatePresence mode="wait">
                        {activeTab === 0 && (
                            <Motion.div
                                key="tab-0"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 10 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.05)', bgcolor: 'rgba(0,0,0,0.02)' }}>
                                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                        <FormControl size="small" sx={{ minWidth: 150 }}>
                                            <InputLabel>{t('common.inventory.warehouse', 'Warehouse')}</InputLabel>
                                            <Select value={warehouseFilter} label={t('common.inventory.warehouse', 'Warehouse')} onChange={(e) => setWarehouseFilter(e.target.value)}>
                                                <MenuItem value="all">{t('common.inventory.allWarehouses', 'All Warehouses')}</MenuItem>
                                                {(warehouses?.items || warehouses || []).map(w => (
                                                    <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl size="small" sx={{ minWidth: 140 }}>
                                            <InputLabel>{t('common.inventory.stockStatus', 'Stock Status')}</InputLabel>
                                            <Select value={stockStatusFilter} label={t('common.inventory.stockStatus', 'Stock Status')} onChange={(e) => setStockStatusFilter(e.target.value)}>
                                                <MenuItem value="all">{t('common.inventory.allStatus', 'All Status')}</MenuItem>
                                                <MenuItem value="in_stock">{t('common.inventory.inStock', 'In Stock')}</MenuItem>
                                                <MenuItem value="low_stock">{t('common.inventory.lowStock', 'Low Stock')}</MenuItem>
                                                <MenuItem value="out_of_stock">{t('common.inventory.outOfStock', 'Out of Stock')}</MenuItem>
                                                <MenuItem value="overstock">{t('common.inventory.overstock', 'Overstock')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <FormControl size="small" sx={{ minWidth: 130 }}>
                                            <InputLabel>{t('common.inventory.category', 'Category')}</InputLabel>
                                            <Select value={categoryFilter} label={t('common.inventory.category', 'Category')} onChange={(e) => setCategoryFilter(e.target.value)}>
                                                <MenuItem value="all">{t('common.inventory.allCategories', 'All Categories')}</MenuItem>
                                                <MenuItem value="electronics">{t('common.inventory.electronics', 'Electronics')}</MenuItem>
                                                <MenuItem value="clothing">{t('common.inventory.clothing', 'Clothing')}</MenuItem>
                                                <MenuItem value="food">{t('common.inventory.foodBeverage', 'Food & Beverage')}</MenuItem>
                                                <MenuItem value="furniture">{t('common.inventory.furniture', 'Furniture')}</MenuItem>
                                                <MenuItem value="other">{t('common.inventory.other', 'Other')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            size="small"
                                            placeholder={t('common.inventory.searchInventory', 'Search inventory...')}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                                            sx={{ minWidth: 200 }}
                                        />
                                        <ToggleButtonGroup
                                            value={viewMode}
                                            exclusive
                                            onChange={handleViewModeChange}
                                            size="small"
                                        >
                                            <ToggleButton value="table" aria-label="Table view">
                                                <Tooltip title={t('common.tableView', 'Table View')}>
                                                    <TableIcon />
                                                </Tooltip>
                                            </ToggleButton>
                                            <ToggleButton value="kanban" aria-label="Kanban view">
                                                <Tooltip title={t('common.kanbanView', 'Kanban View')}>
                                                    <KanbanIcon />
                                                </Tooltip>
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Stack>
                                </Box>
                                {viewMode === 'kanban' ? (
                                    <KanbanView
                                        columns={INVENTORY_STATUS_KANBAN_COLUMNS}
                                        items={kanbanInventory}
                                        loading={invLoading}
                                    />
                                ) : (
                                    <SmartDataGrid
                                        columns={columns}
                                        data={inventoryItems}
                                        loading={invLoading}
                                        onRowClick={handleRowClick}
                                        quickFilters={[
                                            { key: 'all', label: t('common.inventory.allStock', 'All Stock') },
                                            { key: 'low', label: t('common.inventory.lowStock', 'Low Stock'), color: 'warning' },
                                            { key: 'out', label: t('common.inventory.outOfStock', 'Out of Stock'), color: 'error' }
                                        ]}
                                    />
                                )}
                            </Motion.div>
                        )}

                        {activeTab === 1 && (
                            <Motion.div
                                key="tab-1"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 10 }}
                                transition={{ duration: 0.2 }}
                            >
                                <SmartDataGrid
                                    columns={[
                                        { id: 'timestamp', label: 'Time', render: (val) => new Date(val).toLocaleString() },
                                        { id: 'product', label: 'Product', render: (val, row) => row.product_name },
                                        { id: 'type', label: 'Type', render: (val) => <Chip label={val} size="small" variant="outlined" /> },
                                        { id: 'qty', label: 'Qty', align: 'right', render: (val) => <Typography sx={{ fontWeight: 700 }}>{val > 0 ? `+${val}` : val}</Typography> },
                                        { id: 'operator', label: 'Operator', render: (val) => val || '-' }
                                    ]}
                                    data={movements}
                                    loading={movementsLoading}
                                />
                            </Motion.div>
                        )}

                        {/* Other tabs placeholders */}
                        {(activeTab === 2 || activeTab === 3) && (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <InfoIcon sx={{ fontSize: 48, opacity: 0.2, mb: 2 }} />
                                <Typography variant="h6" sx={{ opacity: 0.5 }}>
                                    This visualization is being migrated to the high-fidelity Aurora 2026 engine.
                                </Typography>
                            </Box>
                        )}
                    </AnimatePresence>
                </Box>
            </Paper>

            {/* Integrated Action Sidebar */}
            <GlassSidebar
                isOpen={isSidebarOpen}
                onClose={() => setSidebarOpen(false)}
                title={selectedItem?.name || "-"}
            >
                <Box sx={{ p: 3 }}>
                    <Tabs value={sidebarTab} onChange={(e, v) => setSidebarTab(v)} variant="fullWidth" sx={{ mb: 3 }}>
                        <Tab label={t('common.inventory.details', 'Details')} />
                        <Tab label={t('common.inventory.move', 'Move')} />
                        <Tab label={t('common.inventory.history', 'History')} />
                    </Tabs>

                    {sidebarTab === 0 && selectedItem && (
                        <Motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                            <Box sx={{ mb: 4, p: 3, borderRadius: 3, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">{t('common.inventory.onHand', 'On Hand')}</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 800 }}>{selectedItem.quantity}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">{t('common.inventory.reserved', 'Reserved')}</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 800 }}>{selectedItem.reserved || 0}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1, opacity: 0.5 }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">{t('common.inventory.currentLocation', 'Current Location')}</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{selectedItem.location_code}</Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Button variant="contained" fullWidth sx={{ mb: 2 }}>{t('common.inventory.editListing', 'Edit Listing')}</Button>
                            <Button variant="outlined" fullWidth color="error">{t('common.inventory.discardProduct', 'Discard Product')}</Button>
                        </Motion.div>
                    )}

                    {sidebarTab === 1 && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 2 }}>{t('common.inventory.initiateMovement', 'Initiate an internal stock movement for this SKU.')}</Typography>
                            <TextField fullWidth label={t('common.inventory.destinationLocation', 'Destination Location')} placeholder="e.g. A-02-05" sx={{ mb: 3 }} />
                            <TextField fullWidth label={t('common.inventory.quantityToMove', 'Quantity to Move')} type="number" sx={{ mb: 3 }} />
                            <Button variant="contained" fullWidth startIcon={<MovementIcon />}>{t('common.inventory.confirmInternalMove', 'Confirm Internal Move')}</Button>
                        </Box>
                    )}

                    {sidebarTab === 2 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 2 }}>{t('common.inventory.recentMovements', 'Recent local movements for this SKU')}</Typography>
                                                        {(selectedItem?.movements || []).length > 0 ? (
                                                            (selectedItem?.movements || []).slice(0, 3).map((movement, i) => (
                                    <Box key={movement.id || i} sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.02)' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{movement.type || '-'}</Typography>
                                            <Typography variant="caption">{movement.timestamp || movement.created_at || ''}</Typography>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">{movement.from_location || ''} → {movement.to_location || ''} by {movement.user || ''}</Typography>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('common.inventory.noRecentMovements', 'No recent movements')}</Typography>
                            )}
                            <Button variant="text" fullWidth startIcon={<HistoryIcon />}>{t('common.inventory.viewFullAuditLog', 'View Full Audit Log')}</Button>
                        </Box>
                    )}
                </Box>
            </GlassSidebar>

            <Dialog open={adjustOpen} onClose={() => setAdjustOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>{t('common.inventory.adjustInventoryLevel', 'Adjust Inventory Level')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                        <Autocomplete
                            options={productsData?.items || []}
                            getOptionLabel={(option) => `${option.sku} - ${option.name}`}
                            value={adjustForm.product}
                            onChange={(_, newValue) => setAdjustForm({ ...adjustForm, product: newValue })}
                            renderInput={(params) => <TextField {...params} label={t('common.inventory.selectProduct', 'Select Product')} />}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('common.inventory.action', 'Action')}</InputLabel>
                                    <Select
                                        value={adjustForm.type}
                                        label={t('common.inventory.action', 'Action')}
                                        onChange={(e) => setAdjustForm({ ...adjustForm, type: e.target.value })}
                                    >
                                        <MenuItem value="add">{t('common.inventory.addStock', 'Add Stock (+)')}</MenuItem>
                                        <MenuItem value="remove">{t('common.inventory.removeStock', 'Remove Stock (-)')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={t('common.inventory.quantity', 'Quantity')}
                                    type="number"
                                    fullWidth
                                    value={adjustForm.quantity}
                                    onChange={(e) => setAdjustForm({ ...adjustForm, quantity: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                        <TextField
                            label={t('common.inventory.reasonCode', 'Reason Code')}
                            fullWidth
                            value={adjustForm.reason}
                            onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setAdjustOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={handleAdjustSubmit}
                        disabled={!adjustForm.product || adjustForm.quantity <= 0 || adjustMutation.isPending}
                    >
                        {adjustMutation.isPending ? t('common.processing', 'Processing...') : t('common.inventory.confirmAdjustment', 'Confirm Adjustment')}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default React.memo(InventoryCockpitPage);
